import { CanDeactivateFn, Route } from '@angular/router';
import { LOCALIZED_SHARED_ROUTES, SHARED_ROUTE_IDS } from '@lexplus-shared-lib';
import { IsLoggedInGuard } from '@lexplus-authentication';
import { AdminOnlyGuard } from '../../guards/admin-only.guard';

/**
 * Asks before a detail panel (FAQ, News) is left with unsaved edits, whether for another tab or by the browser's
 * back button. The panel decides, so a save or a confirmed close passes without a second question.
 *
 * @param {{ confirmLeave(): boolean | Promise<boolean> }} panel - The panel being left
 * @returns {boolean | Promise<boolean>} Whether the panel may be left
 */
const leaveDrawerGuard: CanDeactivateFn<{ confirmLeave(): boolean | Promise<boolean> }> = (panel) => panel.confirmLeave();

/**
 * The Administration page and its nine tabs. Every tab is a route of its own so a tab is
 * linkable and survives a reload; they all load the same page component, which reads the
 * active tab from the route's `routeId`.
 */
export function AdministrationPageRoute(localeId: string): Route {
  const locale = localeId?.split('-')[0] || 'en';

  const tabRouteIds = [
    SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_USERS,
    SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_NEWSLETTER,
    SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_NEWS,
    SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_EMAIL_TEMPLATES,
    SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_FAQ,
    SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_PRIVACY_POLICY,
    SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_ROLES,
    SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_CORTEC,
    SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_EMAIL_LOG,
  ];

  return {
    path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION][locale],
    canActivate: [IsLoggedInGuard, AdminOnlyGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_USERS][locale], // Default tab
      },
      ...tabRouteIds.map((routeId) => ({
        path: LOCALIZED_SHARED_ROUTES[routeId][locale],
        loadComponent: () => import('./pages/administration-page/administration-page.component').then((m) => m.AdministrationPageComponent),
        data: { routeId },
        // The detail panels are routes of their own, so they survive a reload and can be linked
        // to — the same shape the user app's Aufgaben drawer uses. Each renders into its tab's
        // outlet, over the overview, which stays mounted behind it.
        children: drawerRoutes(locale, routeId),
      })),
    ],
    // Carried on the parent as well, so switching the language from a tab-less
    // `/administration` still resolves to this page rather than to the dashboard.
    data: { routeId: SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION },
  };
}

/**
 * The detail-panel child routes of a tab — the blank form and one entry — or none for a tab
 * without a panel.
 *
 * `neu` is declared **before** `:id` — the router takes the first match, so the reverse order
 * would resolve «neu» as an entry with that id.
 *
 * @param {string} locale - The two-letter language the paths are localized into
 * @param {string} routeId - The tab's route id, carried on both children so the page keeps
 *   highlighting the tab while the panel is open
 * @returns {Route[]} The child routes
 */
function drawerRoutes(locale: string, routeId: string): Route[] {
  switch (routeId) {
    case SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_USERS:
      return newAndIdRoutes(locale, routeId, SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_USERS_NEW, () =>
        import('./components/administration-benutzer-drawer/administration-benutzer-drawer.component').then(
          (m) => m.AdministrationBenutzerDrawerComponent,
        ),
      );
    case SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_PRIVACY_POLICY:
      return newAndIdRoutes(locale, routeId, SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_PRIVACY_POLICY_NEW, () =>
        import('./components/administration-privacy-policy-drawer/administration-privacy-policy-drawer.component').then(
          (m) => m.AdministrationPrivacyPolicyDrawerComponent,
        ),
      );
    case SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_NEWS:
      return newAndIdRoutes(
        locale,
        routeId,
        SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_NEWS_NEW,
        () =>
          import('./components/administration-news-drawer/administration-news-drawer.component').then(
            (m) => m.AdministrationNewsDrawerComponent,
          ),
        [leaveDrawerGuard],
      );
    case SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_FAQ:
      return newAndIdRoutes(
        locale,
        routeId,
        SHARED_ROUTE_IDS.ADMIN_ADMINISTRATION_FAQ_NEW,
        () =>
          import('./components/administration-faq-drawer/administration-faq-drawer.component').then(
            (m) => m.AdministrationFaqDrawerComponent,
          ),
        [leaveDrawerGuard],
      );
    default:
      return [];
  }
}

/**
 * The blank-form route and the `:id` route of one panel, both loading the same component.
 *
 * @param {string} locale - The two-letter language the paths are localized into
 * @param {string} routeId - The tab's route id, carried on both children
 * @param {string} newRouteId - The route id whose localized segment opens the blank form
 * @param {Route['loadComponent']} loadDrawer - Loads the panel component
 * @param {Route['canDeactivate']} canDeactivate - Guards that may refuse to leave the panel, for one with edits to lose
 * @returns {Route[]} The two child routes
 */
function newAndIdRoutes(
  locale: string,
  routeId: string,
  newRouteId: string,
  loadDrawer: Route['loadComponent'],
  canDeactivate?: Route['canDeactivate'],
): Route[] {
  return [
    {
      path: LOCALIZED_SHARED_ROUTES[newRouteId][locale],
      loadComponent: loadDrawer,
      canDeactivate,
      data: { routeId },
    },
    {
      path: ':id',
      loadComponent: loadDrawer,
      canDeactivate,
      data: { routeId },
    },
  ];
}
